import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ItemCard, type ItemCardData } from './ItemCard';
import {
  resolveCardClosure,
  type Card,
} from '../../services/CardDescriptionService';
import './CardDetail.scss';

function toItemCardData(card: Card): ItemCardData {
  return {
    icon: card.icon,
    name: card.name,
    stats: card.stats,
    lines: card.lines,
  };
}

interface Props {
  card: ItemCardData;
  pos: { top: number; left: number };
}

// keep in sync with .item-card max-width
const CARD_WIDTH = 280;
const STACK_GAP = 12;

export const CardDetail = ({ card, pos }: Props) => {
  const closure = card.rootCard ? resolveCardClosure(card.rootCard) : [];
  const related = closure.slice(1);

  // The main card is centered on `pos.left` but its actual width varies with
  // content (180–280px). Measure it so the related stack sits a fixed gap from
  // the real edge — otherwise the offset drifts per host (different card
  // widths) and reads as inconsistent.
  const mainRef = useRef<HTMLDivElement>(null);
  const [mainWidth, setMainWidth] = useState(CARD_WIDTH);
  useLayoutEffect(() => {
    if (mainRef.current) {
      setMainWidth(mainRef.current.getBoundingClientRect().width);
    }
  }, [card, pos]);

  const halfMain = mainWidth / 2;
  const flipLeft =
    pos.left + halfMain + STACK_GAP + CARD_WIDTH > window.innerWidth;
  const relatedLeft = flipLeft
    ? pos.left - halfMain - STACK_GAP - CARD_WIDTH
    : pos.left + halfMain + STACK_GAP;

  return createPortal(
    <>
      <ItemCard {...card} pos={pos} ref={mainRef} />
      {related.length > 0 && (
        <div
          className="card-detail-related"
          style={{
            top: pos.top,
            left: relatedLeft,
            maxHeight: window.innerHeight - pos.top - 12,
          }}
        >
          {related.map((c) => (
            <ItemCard
              key={`${c.kind}:${c.id}`}
              {...toItemCardData(c)}
              className="card-detail-related-card"
            />
          ))}
        </div>
      )}
    </>,
    document.body,
  );
};
